'use client'

// Platform rule (Veronika, 2026-08-26): EVERY file download requires a logged-in
// member — downloads are vendor leads that EA forwards on. This hook is the single
// gate: components ask it for the member state and the login redirect, and report
// completed downloads so the lead-notification email (Veronika + Gold) fires.
import { useSupabaseAuth } from '@/context/supabaseContext'
import { usePathname } from 'next/navigation'
import { trackEvent } from '@/lib/analytics/posthog'
import { recordMemberActivity } from '@/lib/analytics/memberActivity'

/** Where on the page the tap happened, so reports can tell the surfaces apart. */
export type GateSurface = 'downloads_panel' | 'cta_button' | 'inline_link' | 'tool_embed' | 'tool_button'

export function useDownloadGate() {
  const { user, isLoaded } = useSupabaseAuth()
  const pathname = usePathname()
  const loginHref = `/login?next=${encodeURIComponent(pathname || '/')}`

  // Fire-and-forget lead ping; identity is re-verified server-side from the session
  // cookie. Never blocks or delays the download itself. One email per item per visit:
  // "View" then "Download" of the same file (or a double tap) is one lead, not two.
  const postNotification = (dedupeKey: string, body: Record<string, unknown>) => {
    try {
      const k = `ea_notified:${dedupeKey}`
      if (window.sessionStorage.getItem(k)) return
      window.sessionStorage.setItem(k, '1')
    } catch {
      // storage blocked: send anyway, a duplicate email beats a missed lead
    }
    void fetch('/api/notify-pdf-download', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, pageUrl: `${window.location.origin}${pathname}` }),
    }).catch((error) => {
      console.error('Failed to send member notification:', error)
    })
  }

  const notifyDownload = (payload: {
    pdfFilename: string
    pdfUrl?: string
    entryTitle?: string
    /** 'view' = opened in the browser (PdfCard "View"); the member has the file either way. */
    action?: 'view' | 'download'
  }) => postNotification(`file:${payload.pdfUrl || payload.pdfFilename}`, { kind: 'file', ...payload })

  // Tool access is a lead signal too (2026-09-18: a member signed up only to open the pack
  // calculator and no alert went out).
  const notifyToolOpen = (toolName: string, toolUrl: string) =>
    postNotification(`tool:${toolUrl}`, { kind: 'tool', toolName, toolUrl })

  // Analytics for EVERY gated surface, not only PdfCard (2026-09-18: a member downloaded the
  // white paper twice via the CTA button and PostHog showed nothing; only the alert email
  // knew). Same event names PdfCard uses, plus `surface`, so existing reports keep working.
  // Members opening a tool need nothing here: outbound_link_clicked already records it.
  const trackGateClick = (label: string, surface: GateSurface) =>
    trackEvent('product_sheet_login_gate_click', { file_title: label, page_path: pathname, surface })
  // The member's own record (member_activity, kept on EA's server whatever the banner
  // choice) rides along: target is the file name, else its media path.
  const trackFileOpen = (fileTitle: string, fileUrl: string, surface: GateSurface) => {
    trackEvent('product_sheet_downloaded', {
      file_title: fileTitle,
      file_url: fileUrl,
      page_path: pathname,
      surface,
    })
    void recordMemberActivity('sheet_download', { target: fileTitle || fileUrl })
  }

  return {
    user,
    isLoaded,
    loginHref,
    notifyDownload,
    notifyToolOpen,
    trackGateClick,
    trackFileOpen,
  }
}
